import PropTypes from 'prop-types';

const VARIANT_ALIASES = {
  violet: 'purple',
  sky: 'blue',
  emerald: 'green',
};

/**
 * Centered metric tile: optional icon, label, value and helper text.
 *
 * @param {'neutral'|'purple'|'violet'|'blue'|'sky'|'green'|'emerald'|'amber'|'red'} [props.variant='neutral']
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {'strong'|'subtle'} [props.tone='strong']
 * @param {'label'|'inline'|'stack'} [props.iconPlacement='label']
 * @param {'center'|'left'} [props.align='center']
 * @param {import('react').ReactNode} [props.helperText]
 */
const STRONG_VARIANT_STYLES = {
  neutral: {
    container:
      'border-slate-300/95 bg-slate-100 dark:border-slate-500/70 dark:bg-slate-800/85 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]',
    label: 'text-content-muted',
    value: 'text-content',
    icon: 'text-content-secondary opacity-90 dark:text-content-muted dark:opacity-80',
  },
  purple: {
    container: 'border-purple-500/80 bg-purple-100 dark:border-purple-400/90 dark:bg-purple-950/75',
    label: 'text-purple-800 dark:text-purple-300',
    value: 'text-purple-950 dark:text-purple-200',
    icon: 'text-purple-700 opacity-90 dark:text-purple-400 dark:opacity-90',
  },
  blue: {
    container: 'border-blue-500/80 bg-blue-100 dark:border-blue-400/90 dark:bg-blue-950/75',
    label: 'text-blue-800 dark:text-blue-300',
    value: 'text-blue-950 dark:text-blue-200',
    icon: 'text-blue-700 opacity-90 dark:text-blue-400 dark:opacity-90',
  },
  green: {
    container:
      'border-emerald-500/80 bg-emerald-100 dark:border-emerald-400/90 dark:bg-emerald-950/75',
    label: 'text-emerald-800 dark:text-emerald-300',
    value: 'text-emerald-950 dark:text-emerald-200',
    icon: 'text-emerald-700 opacity-90 dark:text-emerald-400 dark:opacity-90',
  },
  amber: {
    container: 'border-amber-500/80 bg-amber-100 dark:border-amber-400/90 dark:bg-amber-950/75',
    label: 'text-amber-800 dark:text-amber-300',
    value: 'text-amber-950 dark:text-amber-200',
    icon: 'text-amber-700 opacity-90 dark:text-amber-400 dark:opacity-90',
  },
  red: {
    container: 'border-red-500/80 bg-red-100 dark:border-red-400/90 dark:bg-red-950/75',
    label: 'text-red-800 dark:text-red-300',
    value: 'text-red-950 dark:text-red-200',
    icon: 'text-red-700 opacity-90 dark:text-red-400 dark:opacity-90',
  },
};

const SUBTLE_VARIANT_STYLES = {
  neutral: {
    container: 'border-slate-300/95 bg-slate-100 dark:border-edge dark:bg-surface-muted/60',
    label: 'text-content-muted',
    value: 'text-content',
    icon: 'text-content-secondary',
  },
  purple: {
    container: 'border-purple-500/35 bg-purple-50 dark:border-purple-500/25 dark:bg-purple-500/5',
    label: 'text-purple-700 dark:text-purple-300/90',
    value: 'text-purple-950 dark:text-purple-200',
    icon: 'text-purple-600 dark:text-purple-400',
  },
  blue: {
    container: 'border-blue-500/35 bg-blue-50 dark:border-blue-500/25 dark:bg-blue-500/5',
    label: 'text-blue-700 dark:text-blue-300/90',
    value: 'text-blue-950 dark:text-blue-200',
    icon: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    container:
      'border-emerald-500/35 bg-emerald-50 dark:border-emerald-500/25 dark:bg-emerald-500/5',
    label: 'text-emerald-800 dark:text-emerald-300',
    value: 'text-emerald-950 dark:text-emerald-200',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  amber: {
    container: 'border-amber-500/40 bg-amber-50 dark:border-amber-500/25 dark:bg-amber-500/5',
    label: 'text-amber-800 dark:text-amber-300/90',
    value: 'text-amber-950 dark:text-amber-200',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  red: {
    container: 'border-red-500/35 bg-red-50 dark:border-red-500/25 dark:bg-red-500/5',
    label: 'text-red-700 dark:text-red-300/90',
    value: 'text-red-950 dark:text-red-200',
    icon: 'text-red-600 dark:text-red-400',
  },
};

const SIZE_STYLES = {
  sm: {
    root: 'rounded-lg px-3 py-2 gap-0.5',
    stackRoot: 'rounded-lg p-3 gap-2',
    inlineRoot: 'rounded-lg p-3 gap-1.5',
    labelRow: 'text-[10px] font-medium uppercase tracking-wide gap-1.5',
    drawerLabelRow: 'text-[11px] font-medium uppercase tracking-wide gap-1.5',
    icon: 'h-3 w-3',
    drawerIcon: 'size-3.5',
    value: 'text-lg font-semibold tabular-nums leading-tight',
    drawerValue: 'text-xl font-semibold tabular-nums tracking-tight',
    helper: 'text-[10px] leading-snug',
  },
  md: {
    root: 'rounded-xl px-4 py-3 gap-1',
    stackRoot: 'rounded-xl p-4 gap-3',
    inlineRoot: 'rounded-xl p-4 gap-2',
    labelRow: 'text-xs font-medium uppercase tracking-wide gap-2',
    drawerLabelRow: 'text-xs font-medium uppercase tracking-wide gap-2',
    icon: 'h-3.5 w-3.5',
    drawerIcon: 'size-4',
    value: 'text-2xl font-semibold tabular-nums',
    drawerValue: 'text-2xl font-semibold tabular-nums tracking-tight',
    helper: 'text-[11px] leading-snug',
  },
  lg: {
    root: 'rounded-xl px-4 py-4 gap-1 min-w-0 flex-1',
    stackRoot: 'rounded-xl px-4 py-4 gap-3 min-w-0 flex-1',
    inlineRoot: 'rounded-xl px-4 py-4 gap-2 min-w-0 flex-1',
    labelRow: 'text-xs font-medium gap-2',
    drawerLabelRow: 'text-xs font-medium uppercase tracking-wide gap-2',
    icon: 'h-4 w-4',
    drawerIcon: 'size-4',
    value: 'text-2xl font-semibold tabular-nums',
    drawerValue: 'text-2xl font-semibold tabular-nums tracking-tight',
    helper: 'text-[11px] leading-snug',
  },
};

function normalizeVariant(variant) {
  return VARIANT_ALIASES[variant] ?? variant;
}

function getVariantStyles(variant, tone) {
  const normalizedVariant = normalizeVariant(variant);
  const styles = tone === 'subtle' ? SUBTLE_VARIANT_STYLES : STRONG_VARIANT_STYLES;
  return styles[normalizedVariant] ?? styles.neutral;
}

const MetricTile = ({
  label,
  value,
  icon: Icon,
  variant = 'neutral',
  size = 'md',
  tone = 'strong',
  iconPlacement = 'label',
  align = 'center',
  helperText,
  className = '',
  ...props
}) => {
  const v = getVariantStyles(variant, tone);
  const s = SIZE_STYLES[size] ?? SIZE_STYLES.md;
  const isSubtle = tone === 'subtle';
  const isStacked = iconPlacement === 'stack';
  const isInline = iconPlacement === 'inline';
  const isLeftAligned = align === 'left';
  const labelRowClassName = [
    'flex items-center',
    isLeftAligned ? 'justify-start' : 'justify-center',
    isStacked ? s.drawerLabelRow : s.labelRow,
    !isStacked ? 'flex-wrap' : '',
    v.label,
  ]
    .filter(Boolean)
    .join(' ');
  const iconClassName = [isStacked || isInline ? s.drawerIcon : s.icon, 'shrink-0', v.icon]
    .filter(Boolean)
    .join(' ');
  const valueClassName = [isStacked || isInline ? s.drawerValue : s.value, v.value]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={[
        'flex flex-col min-w-0',
        isLeftAligned ? 'items-start text-left' : 'items-center text-center',
        isSubtle ? 'border' : 'border-2',
        v.container,
        isStacked ? s.stackRoot : isInline ? s.inlineRoot : s.root,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {isStacked ? (
        <>
          {Icon ? (
            <div className="flex justify-center">
              <Icon className={iconClassName} aria-hidden />
            </div>
          ) : null}
          <div className={valueClassName}>{value}</div>
          <div className={labelRowClassName}>
            <span className="truncate max-w-full">{label}</span>
          </div>
        </>
      ) : (
        <>
          <div className={labelRowClassName}>
            {Icon ? <Icon className={iconClassName} aria-hidden /> : null}
            <span className="truncate max-w-full">{label}</span>
          </div>
          <div className={valueClassName}>{value}</div>
        </>
      )}
      {helperText ? (
        <p className={[s.helper, 'text-content-muted'].join(' ')}>{helperText}</p>
      ) : null}
    </div>
  );
};

MetricTile.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf([
    'neutral',
    'purple',
    'violet',
    'blue',
    'sky',
    'green',
    'emerald',
    'amber',
    'red',
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  tone: PropTypes.oneOf(['strong', 'subtle']),
  iconPlacement: PropTypes.oneOf(['label', 'inline', 'stack']),
  align: PropTypes.oneOf(['center', 'left']),
  helperText: PropTypes.node,
  className: PropTypes.string,
};

export default MetricTile;
